import { useQuery } from "@tanstack/react-query";
import {
  listProductOfferings,
  listPublishedServices,
} from "@/api/publicCatalog";
import {
  mergeProductOfferingCtas,
  PRODUCT_OFFERING_CTA_FALLBACK as CTA_FALLBACK,
} from "@/lib/productOfferingCtas.mjs";
import {
  mergeLandingSlides,
  type LandingSlide,
} from "@/lib/landingSlides.mjs";

export type CtaProductType = "toy" | "story" | "animation";

export type ProductOfferingCta = {
  productType: CtaProductType;
  name: string;
  nameEn: string | null;
  href: string;
};

export type CustomServiceCta = {
  productType: "custom";
  slug: string;
  name: string;
  description: string | null;
  href: string;
};

export type ProductOfferingsResult = {
  classic: ProductOfferingCta[];
  custom: CustomServiceCta[];
  slides: LandingSlide[];
};

/** Default trio when fetch fails or UI is still loading. */
export const PRODUCT_OFFERING_CTA_FALLBACK: ProductOfferingCta[] = CTA_FALLBACK;

const EMPTY_CUSTOM: CustomServiceCta[] = [];
const FALLBACK_CLASSIC_ROWS = [
  { productType: "toy", name: "", nameEn: null, sortOrder: 0 },
  { productType: "story", name: "", nameEn: null, sortOrder: 1 },
  { productType: "animation", name: "", nameEn: null, sortOrder: 2 },
] as const;

export function useProductOfferings() {
  return useQuery({
    queryKey: ["product-offerings"],
    staleTime: 5 * 60 * 1000,
    queryFn: async (): Promise<ProductOfferingsResult> => {
      try {
        const [rows, published] = await Promise.all([
          listProductOfferings(),
          listPublishedServices().catch(() => []),
        ]);
        return {
          ...mergeProductOfferingCtas(rows, published),
          slides: mergeLandingSlides(rows, published),
        };
      } catch {
        return {
          classic: PRODUCT_OFFERING_CTA_FALLBACK,
          custom: EMPTY_CUSTOM,
          slides: mergeLandingSlides(FALLBACK_CLASSIC_ROWS, []),
        };
      }
    },
  });
}
